<template>
  <div
    v-if="whitelabel && whitelabelPlus"
    class="upgrade-to-whitelabel"
  >
    <div v-if="!whitelabel.owned && !whitelabelPlus.owned">
      <header>
        <div class="container">
          <h3>Get whitelabel</h3>
          <h1 class="text-center">
            Add your own branding.<br>
            Sell your own SEO platform.
          </h1>
          <p class="text-center">
            Harness the power of Hike under your own branding with our white label functionality.
            Fully customisable, easy to get set-up and running.
          </p>
        </div>
      </header>

      <section class="upgrade-title">
        <div class="container">
          <h3>
            Increase sales and improve credibility<br>
            with your ‘own’ SEO platform.
          </h3>
          <p>
            Built to allow our agency customers and resellers to increase their monthly revenue<br>easily. Take a look
            at our main features below.
          </p>
        </div>
      </section>

      <section>
        <div class="container">
          <div class="upgrade-reasons">
            <div class="reason">
              <img src="/img/whitelabel-custom-branding.png">
              <h4>Custom Branding</h4>
              <p>
                Fully customise the platform by having your own custom domain, logo, colours and login imagery.
                Your clients will only ever see your brand, not Hike.
              </p>
            </div>
            <div class="reason">
              <img src="/img/whitelabel-audit-tool.png">
              <h4>Website Audit Tool</h4>
              <p>
                The white label SEO audit tool enables you to easily capture SEO leads directly from your website.
                Embed a form and send your prospects an enticing SEO report!
              </p>
            </div>
            <div class="reason col-width-2">
              <img src="/img/whitelabel-user-management.png">
              <h4>User Management</h4>
              <p>
                Control exactly what your customers see or do within the platform. Choose between read, edit or hide
                access to each feature. Perfect if your customers have different needs.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>

    <section
      :class="[
        'upgrade-options',
        whitelabel.owned || whitelabelPlus.owned ? 'no-banner' : '',
      ]"
    >
      <div class="container">
        <h3
          class="text-center"
          v-html="headerTitle"
        />
        <p
          class="text-center"
          v-html="headerDescription"
        />

        <div
          v-if="hasActiveMerchantCustomers"
          class="disabled-message text-center mb-3"
        >
          <span class="text-bold">Please note:</span> You cannot cancel/downgrade your white-label subscription while
          you have active merchant customers. Ensure all merchant customers are cancelled before proceeding.
        </div>

        <div
          v-if="whitelabelPlus && whitelabel"
          class="whitelabel-options"
        >
          <whitelabel-option
            v-if="whitelabel"
            :price="whitelabel"
            :cancel-button="
              whitelabelPlus.owned
                ? 'Downgrade to White-Label'
                : ( whitelabel.owned ? 'Cancel White-Label' : null )
            "
            :downgrade="whitelabelPlus.owned"
            :loading="loading"
            :disabled="hasActiveMerchantCustomers"
            @cancel="cancelProduct"
            @purchase="purchase"
          />
          <whitelabel-option
            :price="whitelabelPlus"
            :cancel-button="whitelabelPlus.owned ? 'Cancel White-Label Plus' : null"
            :loading="loading"
            :disabled="hasActiveMerchantCustomers"
            @cancel="cancelProduct"
            @purchase="purchase"
          />
        </div>
      </div>
    </section>

    <FeatureComparison />
  </div>
</template>

<script>
  import Swal from 'sweetalert2';
  import axios from 'axios';
  import WhitelabelOption from "./WhitelabelOption";
  import FeatureComparison from "@/components/whitelabel/FeatureComparison.vue";

  export default {
    components: {
      FeatureComparison,
      WhitelabelOption,
    },
    props: {
      hasActiveMerchantCustomers: {
        type: Boolean,
        required: false,
        default: false,
      }
    },
    data() {
      return {
        whitelabel: null,
        whitelabelPlus: null,
        loading: false,
      };
    },
    computed: {
      headerTitle() {

        if (this.whitelabel?.owned) {
          return 'Thanks for using White-Label with Hike!';
        }
        return 'Re-brand Hike &amp; sell<br>more SEO';
      },
      headerDescription() {
        if (this.whitelabel?.owned) {
          return 'You currently have access to all of our whitelabel features, including custom branding and our '+
            'website auditing tool';
        }
        return 'Upgrade to white-label to fully rebrand the Hike platform and get access to the audit tool.';
      },
    },
    mounted() {
      this.fetchWhitelabelProducts();
    },
    methods: {
      async fetchWhitelabelProducts() {
        const { data } = await axios.get('/whitelabel/products');
        const { whitelabel, whitelabel_plus } = data;
        this.whitelabel = whitelabel;
        this.whitelabelPlus = whitelabel_plus;
      },
      purchase(product) {
        this.loading = true;
        // TODO: Can probably do better than string comparison here
        if (product.name === 'White-Label') {
          window.addonPurchaser.purchaseWhitelabel().then(() => {
            this.loading = false;
          });
        }
        if (product.name === 'White-Label Plus') {
          window.addonPurchaser.purchaseWhitelabelPlus().then(() => {
            this.loading = false;
          });
        }
      },
      cancelProduct(product, downgrade) {
        this.loading = true;
        let cancelText = `${downgrade ? 'Downgrading to' : 'Cancelling'} ${product.name} will result in reduced functionality and loss of access to some features.`;

        Swal.fire({
          icon: "warning",
          title: "Are you sure?",
          text: cancelText,
          confirmButtonColor: '#B23737',
          showCancelButton: true,
          showLoaderOnConfirm: true,
          backdrop: true,
          preConfirm: async () => {
            if (product.name === 'White-Label' && downgrade) {
              return 'downgrade';
            } else {
              return await window.addonPurchaser.purchaseByUrl('/purchase-addon/cancel-whitelabel')
            }
          },
          allowOutsideClick: () => !Swal.isLoading()
        }).then((result) => {
          if (result.isConfirmed) {
            if (result.value === 'downgrade') {
              Swal.close();
              window.addonPurchaser.purchaseWhitelabel().then(() => {
                this.loading = false;
              });
              return;
            }

            const { value: purchaseResponse } = result;
            const { message, detail, redirect } = purchaseResponse;
            Swal.fire({
              icon: 'success',
              title: message,
              html: detail,
            }).then(function () {
              if (redirect) {
                window.location.href = redirect;
                return;
              }
              window.location.reload();
            });
          }
          this.loading = false;
        }).catch(error => {
          const serverError = {
            message: 'Something went wrong',
            errors: ['Sorry, it looks like we cannot complete your request right now. Please try again.']
          };
          const errors = error.status === 500 ? serverError : (JSON.parse(error.responseText))
          Swal.fire({
            icon: 'error',
            title: errors.message,
            html: errors.errors[0],
          }).then(() => this.loading = false)
        });
      }
    },
  }
</script>

<style lang="scss" scoped>
  .upgrade-to-whitelabel {
    width: 100vw;

    header {
      background: #F8F7F5;
      padding: 6rem 0;

      h1,
      h3,
      p {
        max-width: 585px;
        margin: 0 auto;
      }

      h1 {
        font-weight: 700;
        font-size: 3rem;
        line-height: 3.75rem;
        text-align: center;
        letter-spacing: -2px;
        margin-bottom: 1rem;
      }

      h3 {
        font-size: 0.75rem;
        font-weight: 500;
        line-height: 1rem;
        text-align: center;
        letter-spacing: 3px;
        text-transform: uppercase;
        margin-bottom: 2rem;
      }

      .video_preview {
        filter: drop-shadow(0px 0px 2rem rgba(0, 0, 0, 0.2));
        margin: 4rem auto 0 auto;

        .top_bar {
          height: 2rem;
          background: #070606;
          width: 100%;
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          display: flex;
          padding-top: 10px;
          padding-left: 10px;

          .circle {
            width: 13px;
            height: 13px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 100%;
            margin-right: 9px;
          }
        }
      }
    }

    section {
      background: #fff;

      &.upgrade-title {
        padding: 6rem 0;
        text-align: center;

        h3 {
          font-style: normal;
          font-weight: 500;
          font-size: 2rem;
          line-height: 2.5rem;
        }

        p {
          margin: 0;
        }
      }
    }

    p {
      color: #4E4E54;
    }
  }

  .upgrade-options {
    padding: 4rem 0;
    background: #F8F7F5;

    &.no-banner {
      margin-top: 2rem;
    }

    h3 {
      font-style: normal;
      font-weight: 500;
      font-size: 2rem;
      line-height: 2.5rem;
    }

    p {
      margin: 0 0 2rem 0;
    }
  }

  .upgrade-reasons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2.313rem;
    padding: 0 0 4rem 0;

    .reason {
      text-align: center;
      padding: 0 2rem;

      img {
        margin-bottom: 1rem;
        max-width: 100%;
      }

      h4 {
        font-weight: 700;
        font-size: 1.5rem;
        line-height: 2.125rem;
      }
      &.col-width-2 {
        grid-column: span 2;
        width: 50%;
        margin: 0 auto;
      }
    }
  }

  @media screen and (max-width: 767px) {
    .upgrade-reasons {
      grid-template-columns: repeat(1, 1fr);
    }
  }

  .whitelabel-options {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    padding: 0;
    gap: 40px;

    @media screen and (max-width: 767px) {
      flex-direction: column;
    }
  }

  .disabled-message {
    color: red;
  }
</style>
